import { InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material'
import ProfilePhoto from 'components/ProfilePhoto/ProfilePhoto'
import { OnboardingStep } from 'api/model'
import { useRecoilValue } from 'recoil'
import { twinsAtom } from 'state/twins'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import { useNavigate } from 'react-router-dom'
import { getTextWithLinks } from '../Conversations/Conversations'
import { useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import { COLORS } from 'app-constants'

const AddConversationPopover = ({
  twinId,
  onClose,
  setConversationId,
}: {
  twinId?: string // used when we only want to show a specific twin that we start a conversation with
  onClose: () => void
  updateOnboardingStep: () => void
  setConversationId: React.Dispatch<React.SetStateAction<string>>
}) => {
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  const twins = useRecoilValue(twinsAtom).filter((twin) => {
    if (twinId) {
      return twin.id === twinId
    } else {
      return true
    }
  })

  const [searchText, setSearchText] = useState('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  // filter twins based on search text and twinId
  const filteredTwins = twins.filter((twin) => {
    if (twinId) {
      return twin.id === twinId
    } else {
      return (
        twin.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        twin.user.name?.toLowerCase().includes(searchText.toLowerCase())
      )
    }
  })

  return (
    <Stack py={2} px={1} width="336px">
      <TextField
        placeholder="Search for a twin"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                style={{
                  marginRight: '-8px',
                  color: COLORS.GRAY_3,
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          px: 1,
          marginBottom: '16px',
          input: {
            color: 'secondary.main',
            fontSize: '14px',
            fontWeight: 400,
            paddingLeft: '8px',
          },
          '& ::placeholder': {
            color: 'secondary.main',
            fontSize: '14px',
            fontWeight: 400,
          },
        }}
      />

      <Stack
        position="relative"
        sx={{
          maxHeight: isMobile ? '50vh' : 'auto',
          overflowY: 'scroll',
        }}>
        {filteredTwins.length > 0 ? (
          filteredTwins.map((twin) => (
            <Stack
              key={twin.id}
              borderRadius="8px"
              alignItems="center"
              onClick={() => {
                navigate(`/jelledbots?twinId=${twin.id}`)
                // clear conversation id since we are starting a new conversation
                setConversationId('')
                onClose()
              }}
              py={1}
              px={1}
              direction="row"
              sx={{
                width: isMobile ? '100%' : 'inherit',
                flex: isMobile ? 1 : '0 0 31%',
                backgroundColor: COLORS.PURE_WHITE,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: COLORS.GRAY_7,
                },
              }}>
              <ProfilePhoto
                src={twin.picture || twin.user.picture}
                size="32px"
                name={twin.name || twin.user.name || twin.user.email}
              />
              <Stack ml={1}>
                <Typography variant="pSM">{twin.name || `${twin.user.name}'s twin`}</Typography>
                <Tooltip title={twin.public_summary} enterTouchDelay={0}>
                  <Typography
                    variant="pXS"
                    sx={{
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'pre-line',
                      color: COLORS.GRAY_3,
                    }}>
                    {twin.public_summary ? getTextWithLinks(twin.public_summary) : 'No public summary available.'}
                  </Typography>
                </Tooltip>
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography
            variant="pXS"
            textAlign="center"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            No twins found
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default withOnboardingStep(AddConversationPopover, OnboardingStep.start_conversation)
